import { track } from '@/lib/analytics';
import { useTRPC } from '@/lib/trpc';
import { AnalyticsEvents } from '@beacon/analytics-events';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ACTIONS, EVENTS, Joyride, STATUS, type EventData } from 'react-joyride';
import { beaconOptions, beaconStyles } from './beaconJoyride';
import { TourTooltip } from './TourTooltip';
import { buildJoyrideSteps, tourRegistry } from './tours';
import type { BeaconStepData } from './types';
import { waitForElement } from './waitForElement';

interface TourContextValue {
  activeTourId: string | null;
  start: (tourId: string) => void;
  stop: () => void;
}

const TourContext = createContext<TourContextValue>({
  activeTourId: null,
  start: () => {},
  stop: () => {},
});

export function useTour() {
  return useContext(TourContext);
}

/**
 * Provider for guided tours across the application.
 *
 * Renders nothing until a tour is started via useTour().start(id)
 */
export function TourProvider({ children }: { children: ReactNode }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const [tourId, setTourId] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [run, setRun] = useState(false);

  const markCompleted = useMutation({
    ...trpc.showcase.markCompleted.mutationOptions(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.user.me.queryKey() }),
  });

  const steps = useMemo(
    () => (tourId && tourRegistry[tourId] ? buildJoyrideSteps(tourRegistry[tourId]) : []),
    [tourId],
  );
  const currentData = steps[stepIndex]?.data as BeaconStepData | undefined;

  const start = useCallback((id: string) => {
    if (!tourRegistry[id]) return;

    setTourId(id);
    setStepIndex(0);
    setRun(true);
    track(AnalyticsEvents.SHOWCASE_STARTED, { key: id });
  }, []);

  const finish = useCallback(
    (via: 'finish' | 'skip') => {
      if (tourId) {
        markCompleted.mutate({ key: tourId });
        track(AnalyticsEvents.SHOWCASE_COMPLETED, { key: tourId, via, stepsSeen: stepIndex + 1 });
      }
      setRun(false);
      setTourId(null);
      setStepIndex(0);
    },
    [tourId, stepIndex, markCompleted],
  );

  const stop = useCallback(() => finish('skip'), [finish]);

  const handleEvent = useCallback(
    (data: EventData) => {
      const { action, index, status, type } = data;

      if (status === STATUS.FINISHED) return finish('finish');
      if (status === STATUS.SKIPPED) return finish('skip');
      if (type === EVENTS.TARGET_NOT_FOUND) return finish('skip');

      if (type === EVENTS.STEP_AFTER) {
        const next = index + (action === ACTIONS.PREV ? -1 : 1);

        if (next >= steps.length) return finish('finish');
        setStepIndex(Math.max(0, next));
      }
    },
    [finish, steps.length],
  );

  // navigate to the step's route before showing it
  useEffect(() => {
    if (!tourId || !run) return;

    const route = currentData?.route;

    if (route && location.pathname !== route) navigate(route);
  }, [tourId, run, currentData?.route, location.pathname, navigate]);

  // for click-advance steps, advance when the user clicks the highlighted target
  useEffect(() => {
    if (!tourId || !run || currentData?.advance !== 'click' || !currentData.targetSelector) return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    waitForElement(currentData.targetSelector).then((el) => {
      if (cancelled || !el) return;
      const handler = () => setStepIndex((i) => i + 1);

      el.addEventListener('click', handler, { once: true, capture: true });
      cleanup = () => el.removeEventListener('click', handler, { capture: true });
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [tourId, run, stepIndex, currentData?.advance, currentData?.targetSelector]);

  // analytics: one event per step shown (fires for step 0 when a tour starts)
  useEffect(() => {
    if (tourId && run) track(AnalyticsEvents.SHOWCASE_STEP_VIEWED, { key: tourId, step: stepIndex });
  }, [tourId, run, stepIndex]);

  const value = useMemo<TourContextValue>(() => ({ activeTourId: tourId, start, stop }), [tourId, start, stop]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {tourId && (
        <Joyride
          continuous
          run={run}
          stepIndex={stepIndex}
          steps={steps}
          onEvent={handleEvent}
          tooltipComponent={TourTooltip}
          styles={beaconStyles}
          options={beaconOptions}
          floatingOptions={{ hideArrow: true }}
        />
      )}
    </TourContext.Provider>
  );
}
